import React from 'react';
import PropTypes from 'prop-types';
import CwTriggerField from '../cwtriggerfield/CwTriggerField';
import CwColorPalette from './CwColorPalette';
import css from './CwColorField.scss';
import {setMenuPositions} from '../../utils/CwDropdownService.js';

class CwColorField extends React.Component {
  constructor() {
    super();
    this.state = {
      selectedColor: null,
      selectedColorName: '',
      childVisible: false,
    };

    this.mouseIsDownOnPalette = false;

    this.keyDownHandlers = {
      ArrowDown(event) {
        event.preventDefault(); // prevent window scrolling
        this.expandChild();
      },

      Escape() {
        this.collapseChild();
      }
    };
  }

  mouseDownHandler = () => {
    this.mouseIsDownOnPalette = true;
  };

  mouseUpHandler = () => {
    this.mouseIsDownOnPalette = false;
  };

  expandChild() {
    this.setState({
      childVisible: true
    });
  }

  collapseChild() {
    this.setState({
      childVisible: false
    });
  }

  colorLookup(value) {
    const colors = this.props.colors;
    let colorName = '';

    // Lookup the name of the color based on its id
    colors.forEach((color) => {
      if (value.toUpperCase() === color.id.toUpperCase())
        colorName = color.name;
    });

    if (colorName === '') {
      colorName = value;
    }

    // Update the receieved color to the component's state in order to display it
    this.setState({
      selectedColor: this.colorHexLookup(value) ? this.colorHexLookup(value) : value,
      selectedColorName: colorName,
    });
  }

  colorHexLookup = (value) => {
    let colorHex;
    const colors = this.props.colors;
    colors.forEach((color) => {
      if (value === color.name) {
        colorHex = color.id;
      }
    });
    return colorHex;
  }

  isValidColor = (value) => {
    const regex = new RegExp('^#([A-F0-9]{6})$', "gm");
    const regexValid = regex.test(value);
    const isDefinedColor = this.colorHexLookup(value);
    return (regexValid || isDefinedColor);
  }

  componentDidMount() {
    // Display value's name and color if already given one when color picker re-mounts (i.e. after pod collapses then re-expands)
    if (this.props.value) {
      this.colorLookup(this.props.value);
    }
    window.addEventListener('scroll', this.handleScroll, true);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll, true);
  }

  componentDidUpdate = (prevProps, prevState) => {
    if (this.state.childVisible === true && prevState.childVisible === false) {
      const menuPositions = setMenuPositions(this.cwTriggerField.cwTextField.inputField);

      this.setState({
        menuTop: menuPositions.menuTop,
        menuLeft: menuPositions.menuLeft
      });
    }
  };

  handleScroll = () => {
    if (this.mouseIsDownOnPalette) {
      return;
    }

    this.collapseChild();
  };

  componentWillReceiveProps(nextProps) {
    // Display value's name and color if receiving it from the parent
    if (!nextProps.value || nextProps.value === '') {
      this.setState({
        selectedColor: null,
        selectedColorName: '',
      });
    }
    else {
      this.colorLookup(nextProps.value);
    }
  }

  setIgnoreBlur = (ignore) => {
    this._ignoreBlur = ignore;
  };

  onTriggerClicked = () => {
    this.setState({ childVisible: true });
  };

  handleInputFocus = () => {
    if (this._ignoreBlur) {
      this.setIgnoreBlur(false);
      return;
    }

    // Bring up hex to edit if focused
    this.setState({
      selectedColorName: this.state.selectedColor
    });
    
    if (this.props.onFocus) {
      this.props.onFocus();
    }
  };

  handleInputBlur = () => {
    if (this._ignoreBlur)
      return;
    this.collapseChild();
    this.colorLookup(this.state.selectedColorName);

    // Check if entered value is valid
    if (this.isValidColor(this.state.selectedColorName)) {
      // Check if it's a new value
      if (this.state.selectedColorName !== this.props.value) {
        // Use looked up hex if preset, else use entered custom hex
        const colorHex = this.colorHexLookup(this.state.selectedColorName);
        if (colorHex) {
          this.props.onChange(colorHex);
        }
        else {
          this.props.onChange(this.state.selectedColorName);
        }
      }
    }
    else { // Blank it out if invalid
      this.setState({
        selectedColor: null,
        selectedColorName: ''
      });
      this.props.onChange('');
    }

    if (this.props.onBlur) {
      this.props.onBlur();
    }
  };

  handleClick = (color) => {
    this.props.onChange(color.id);
    this.focus();
    this.setState({
      selectedColor: color.id,
      selectedColorName: color.name,
    });
    this.collapseChild();
  }

  handleKeyDown = (event) => {
    if (this.keyDownHandlers[event.key])
      this.keyDownHandlers[event.key].call(this, event);
  }

  handleChange = (enteredText) => {
    this.setState({
      selectedColor: enteredText,
      selectedColorName: enteredText.toUpperCase()
    });
  }

  focus() {
    this.cwTriggerField.cwTextField.focus();
  }

  render() {
    const selectedColorSwatch = (
      <div style={{ backgroundColor: this.state.selectedColor }}
        className={css.swatchSelected}
      />);

      const style = { // Pass calculated position coordinates to calendar with inline style
        left: this.state.menuLeft,
        top: this.state.menuTop
      };

    return (
      <div className={css.colorField} data-cwid={this.props.cwId}>
        <CwTriggerField triggerIcon={this.props.triggerIcon}
          cwId={this.props.cwId + '_trigger'}
          ref={(input) => this.cwTriggerField = input}
          onKeyDown={this.handleKeyDown}
          onChange={this.handleChange}
          onTrigger={this.onTriggerClicked}
          onFocus={this.handleInputFocus}
          onBlur={this.handleInputBlur}
          value={this.state.selectedColorName}
          className={css.colorFieldTriggerField}
          customElement={selectedColorSwatch}
          regex={'^([\b^A-F,a-f,0-9,#])*$'}
          maxLength={7}
          invalid={this.props.invalid}
          disabled={this.props.disabled}
        />
        {
          this.state.childVisible ?
            <CwColorPalette cwId={this.props.cwId + '_palette'}
              style={style}
              onClick={this.handleClick}
              onMouseDown={this.setIgnoreBlur} // Ignore blur to prevent palette from de-rendering before we can process click
              colors={this.props.colors} />
            : null
        }
      </div>
    );
  }

}

CwColorField.propTypes = {
  /**
   * Connectwise element id to be used as unique identifier
   */
  cwId: PropTypes.string,
  /**
   *  Url of the color picker trigger
   */
  triggerIcon: PropTypes.string,
  /**
   * Our initial color, can be null
   */
  value: PropTypes.string,
  /**
   * A function to handle updating the color picker should at least update the value
   */
  onChange: PropTypes.func,
  /**
   * Array of colors to be displayed in the color palette. "id" must be equal to the hex of the color. "name" is the text displayed in the trigger field.
   */
  colors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    })
  ),
  /**
   * Callback function that is fired when the component is focused
   */
  onFocus: PropTypes.func,
  /**
   * Callback function that is fired when the component is blurred
   */
  onBlur: PropTypes.func,
  /**
   * Used to control if colorfield is valid
   */
  invalid: PropTypes.bool,
  /**
   * Used to disable colorfield
   */
  disabled: PropTypes.bool
};

export default CwColorField;


// WEBPACK FOOTER //
// ./src/components/cwcolorfield/CwColorField.jsx